import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { LoginRequest, LicenseEnv } from '../types';
import {
  login as apiLogin,
  licenseLogin,
  licenseLogout,
  seedLicenseProdTokens,
  setLicenseEnv,
  getLicenseEnv,
  hasLicenseToken,
} from '../services/api';

interface AuthContextType {
  // Authenticated for the CURRENT license environment (the primary module).
  isAuthenticated: boolean;
  // Whether prod/customer (Quota/SAR) modules are usable. False in a DEV-only session.
  hasProdSession: boolean;
  // Active license environment for this session.
  licenseEnv: LicenseEnv;
  isLoading: boolean;
  login: (credentials: LoginRequest, env: LicenseEnv) => Promise<boolean>;
  logout: () => void;
  // Re-evaluate auth state after an in-module env switch (EnvBadge).
  notifyAuthChanged: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const clearProdSession = () => {
  localStorage.removeItem('prodAccessToken');
  localStorage.removeItem('prodRefreshToken');
  localStorage.removeItem('customerAccessToken');
  localStorage.removeItem('customerRefreshToken');
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  // Bumped whenever tokens/env change so the derived values below recompute.
  const [, setAuthVersion] = useState(0);

  const notifyAuthChanged = useCallback(() => setAuthVersion((v) => v + 1), []);

  useEffect(() => {
    // Existing PROD sessions: seed the license PROD token from the customer login.
    seedLicenseProdTokens();
    setIsLoading(false);
  }, []);

  const login = async (credentials: LoginRequest, env: LicenseEnv): Promise<boolean> => {
    try {
      setIsLoading(true);
      if (env === 'PROD') {
        await apiLogin(credentials); // dual prod + customer login; seeds license PROD token
        setLicenseEnv('PROD');
        notifyAuthChanged();
        return hasLicenseToken('PROD');
      }
      // DEV-only session: authenticate ONLY against the DEV license API. Wipe any prod
      // session so no prod/customer call can happen while testing against DEV.
      clearProdSession();
      licenseLogout('PROD');
      const ok = await licenseLogin('DEV', credentials);
      setLicenseEnv('DEV');
      notifyAuthChanged();
      return ok && hasLicenseToken('DEV');
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearProdSession();
    licenseLogout('PROD');
    licenseLogout('DEV');
    setLicenseEnv('PROD');
    notifyAuthChanged();
  };

  const licenseEnv = getLicenseEnv();
  const value: AuthContextType = {
    licenseEnv,
    isAuthenticated: hasLicenseToken(licenseEnv),
    hasProdSession: !!(localStorage.getItem('prodAccessToken') && localStorage.getItem('customerAccessToken')),
    isLoading,
    login,
    logout,
    notifyAuthChanged,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
